import React, { createContext, useContext } from "react";

import sv from "./translations/sv.json";
import en from "./translations/en.json";
import de from "./translations/de.json";

const translations = { sv, en, de };

export const SUPPORTED_LANGS = ["sv", "en", "de"];
export const DEFAULT_LANG = "sv";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  t: (key) => key,
  localePath: (path) => path,
});

function getNestedValue(obj, path) {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

export function LanguageProvider({ lang, children }) {
  const currentLang = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;

  const t = (key) => {
    const value = getNestedValue(translations[currentLang], key);
    if (value !== undefined) return value;

    // Fallback to Swedish
    const fallback = getNestedValue(translations[DEFAULT_LANG], key);
    if (fallback !== undefined) return fallback;

    if (process.env.NODE_ENV === "development") {
      console.warn(`Missing translation key: ${key}`);
    }
    return key;
  };

  const localePath = (path) => {
    if (currentLang === DEFAULT_LANG) return path;
    // Ensure path starts with /
    const cleanPath = path.startsWith("/") ? path : "/" + path;
    return "/" + currentLang + cleanPath;
  };

  return (
    <LanguageContext.Provider value={{ lang: currentLang, t, localePath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function detectLangFromPath(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGS.includes(segments[0]) && segments[0] !== DEFAULT_LANG) {
    return segments[0];
  }
  return DEFAULT_LANG;
}
